/**
 * Formatação da Tela de Leads v7.
 *
 * §6.2: o backend manda dinheiro em CENTAVOS inteiros e percentual já em
 * escala 0-100. O front SÓ formata — não recalcula divisão nenhuma, para não
 * existirem duas fontes de verdade (foi assim que a tela anterior mostrou
 * engajamento de 117,5%).
 */

const NF = new Intl.NumberFormat('pt-BR');
const NF2 = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
});

/** Inteiro com separador de milhar. */
export const n = (v: number | null | undefined): string =>
    NF.format(Math.round(v || 0));

/** Percentual já em escala 0-100 vindo do backend. */
export const pct = (v: number | null | undefined, casas = 1): string =>
    `${(v || 0).toFixed(casas).replace('.', ',')}%`;

/**
 * Centavos -> "US$ 1.234,56". Sempre com os centavos.
 *
 * Arredondava para o dólar inteiro, e isso é perda de informação numa tela de
 * decisão: a origem devolve em centavos e o backend faz a divisão entre páginas
 * em aritmética inteira de centavos, então o dado existe com essa precisão o
 * caminho todo — só a exibição jogava fora.
 */
export const usd = (centavos: number | null | undefined, moeda = 'USD'): string => {
    const simbolo = moeda === 'USD' ? 'US$' : moeda;
    return `${simbolo} ${NF2.format((centavos || 0) / 100)}`;
};

/** Centavos -> "US$ 1,23". Para LTV, que é valor pequeno por pessoa. */
export const usd2 = (centavos: number | null | undefined, moeda = 'USD'): string => {
    const simbolo = moeda === 'USD' ? 'US$' : moeda;
    return `${simbolo} ${((centavos || 0) / 100).toFixed(2).replace('.', ',')}`;
};

/** "2026-08-26" -> "26/08". */
export const diaCurto = (iso: string): string => {
    const [, m, d] = iso.split('-');
    return `${d}/${m}`;
};

/** Hora inteira -> "07h". */
export const horaCurta = (h: number): string => `${String(h).padStart(2, '0')}h`;

/** Rótulo do período para os subtítulos dos blocos. */
export const rotuloPeriodo = (periodo: string, dias: number): string => {
    switch (periodo) {
        case 'hoje': return 'hoje';
        case 'ontem': return 'ontem';
        case '7d': return '7 dias';
        case '30d': return '30 dias';
        case 'mes': return 'este mês';
        default: return `${dias} dias`;
    }
};

/**
 * §4.4: verde é bom, vermelho é ruim, e isso INVERTE conforme a métrica.
 * Em captação, subir é verde; em perdas por 551, subir é vermelho.
 * `maiorEhMelhor=false` para métricas onde crescer é ruim.
 */
export const classeDelta = (variacao: number, maiorEhMelhor = true): string => {
    if (!variacao) return 'lx-flat';
    const bom = maiorEhMelhor ? variacao > 0 : variacao < 0;
    return bom ? 'lx-up' : 'lx-down';
};

/** Seta do delta. Nunca é o único portador de significado — anda com a cor (§4.4). */
export const setaDelta = (variacao: number): string => (variacao >= 0 ? '▲' : '▼');
